import { Color3, Color4, PhysicsMotionType, PhysicsShapeType, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  CameraConfig,
  CollectibleDistributionConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
  WaterConfig,
  WayGenerationConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { GroundConfig, HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateOrthographicCity implements LevelTemplate {
  create(): LevelConfig {
    const probabilityMin = 0.05;
    const probabilityMid = 0.1;
    const probabilityMax = 0.4;

    const skyboxConfig: SkyBoxConfig = {
      //url: 'assets/skybox/skybox_12.jpg',
      clearColor: new Color4(0.5, 0.5, 0.75, 1),
    };
    const fogConfig = new FogConfig();
    const lightConfig = new LightConfig();
    //lightConfig.position = new Vector3(100, 100, 100);
    //lightConfig.direction = new Vector3(1, -1, 0);

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
    };

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 10,
      textures: [
        { url: 'assets/textures/road_chappy.jpg', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground2.avif', uScale: 1, vScale: 1 },
        { url: 'assets/textures/dirt-ground.avif', uScale: 4, vScale: 0.5 },
        { url: 'assets/textures/green-grass.jpg', uScale: 40, vScale: 40 },
        { url: 'assets/textures/stone-ground.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground2.jpg', uScale: 2, vScale: 2 },
        { url: 'assets/textures/stone-ground-colored.jpg', uScale: 3, vScale: 3 },
        { url: 'assets/textures/wood-ground.jpg', uScale: 2, vScale: 0.3 },
        { url: 'assets/textures/cartoon2/Grass1_Light.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/Grass3_Light.png', uScale: 1, vScale: 1 },
        { url: 'assets/textures/cartoon2/Grass5_Light.png', uScale: 1, vScale: 1 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const wayConfig: WayGenerationConfig = {
      width: 9,
      height: 0.5,
      //shape: PathFactory.shapeHalfpipe(),
      segmentsAcross: 12,
      yOffset: 0,
      material: 'road_chappy',
      sideMaterial: 'dirt-ground',
      gapProbability: 0,
      gapSize: { min: 1, max: 2 },
    };

    const pathConfig: PathConfig = {
      start: new Vector3(0, 0, 0),
      destination: new Vector3(0, 0, 500),
      pathPoints: 10,
      segmentsPerCurve: 10,
      pathCurvature: 0,
      pathHilliness: 0,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
    };

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: false,
      orthographic: true,
      orthographicZoom: 10,
      orthographicAlpha: -0.2, // -Math.PI / 16,
      orthographicBeta: 1.4, // Math.PI / 2.2,
      orthographicHorizontalOffset: 12,
      orthographicVerticalOffset: -6,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 1, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const groundConfig: GroundConfig = GroundConfig.builder()
      .size(new Vector3(500, 1500, 1))
      .subdivisions(1)
      .position(new Vector3(0, -1, 0))
      .materialName('green-grass')
      .build();

    const waterConfig: WaterConfig = {
      level: -6,
      depth: 50,
      size: 500,
      effect: true,
      mesh: true,
      renderTargetSize: new Vector2(512, 512),
      bumpTextureUrl: 'assets/textures/waterbump2.png',
      bumpScale: 10,
      windForce: 1,
      waveHeight: 0.1,
      waveLength: 0.1,
      bumpHeight: 0.1,
      windDirection: new Vector2(1, 1),
      waterColor: new Color3(0, 0, 221 / 255),
      colorBlendFactor: 0.2,
      alpha: 0.75,
    };

    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('treeA', AssetType.MESH, 'assets/model/nature/', 'tree_cone.glb'),
        new Asset('treeB', AssetType.MESH, 'assets/model/nature/', 'tree_default.glb'),
        new Asset('treeC', AssetType.MESH, 'assets/model/nature/', 'tree_fat_fall.glb'),
        new Asset('treeD', AssetType.MESH, 'assets/model/nature/', 'tree_palm.glb'),
        new Asset('treeE', AssetType.MESH, 'assets/model/nature/', 'tree_pineDefaultA.glb'),
        new Asset('treeF', AssetType.MESH, 'assets/model/nature/', 'tree_oak.glb'),
        new Asset('treeG', AssetType.MESH, 'assets/model/nature/', 'tree_plateau_fall.glb'),
        new Asset('treeH', AssetType.MESH, 'assets/model/nature/', 'tree_small_dark.glb'),
        new Asset('rockA', AssetType.MESH, 'assets/model/survival/', 'rock-a.glb'),
        new Asset('rockB', AssetType.MESH, 'assets/model/nature/', 'rock_tallB.glb'),
        new Asset('stones', AssetType.MESH, 'assets/model/nature/', 'path_stone.glb'),
        new Asset('log', AssetType.MESH, 'assets/model/nature/', 'log.glb'),
        new Asset('rail', AssetType.MESH, 'assets/model/racing/', 'railDouble.glb'),
        new Asset('billboard', AssetType.MESH, 'assets/model/racing/', 'billboard.glb'),
        new Asset('flag', AssetType.MESH, 'assets/model/racing/', 'flagGreen.glb'),
        new Asset('stand', AssetType.MESH, 'assets/model/racing/', 'grandStandAwning.glb'),
        new Asset('dirt', AssetType.MESH, 'assets/model/nature/', 'crops_dirtRow.glb'),
        new Asset('grass', AssetType.MESH, 'assets/model/nature/', 'grass_large.glb'),
        new Asset('flowerYellow', AssetType.MESH, 'assets/model/nature/', 'flower_yellowA.glb'),
        new Asset('flowerRed', AssetType.MESH, 'assets/model/nature/', 'flower_redB.glb'),
        new Asset('flowerPurple', AssetType.MESH, 'assets/model/nature/', 'flower_purpleC.glb'),
        new Asset('buildingA', AssetType.MESH, 'assets/model/city_commercial/', 'building-a.glb'),
        new Asset('buildingB', AssetType.MESH, 'assets/model/city_commercial/', 'building-b.glb'),
        new Asset('buildingC', AssetType.MESH, 'assets/model/city_commercial/', 'building-c.glb'),
        new Asset('buildingD', AssetType.MESH, 'assets/model/city_commercial/', 'building-d.glb'),
        new Asset('buildingE', AssetType.MESH, 'assets/model/city_commercial/', 'building-e.glb'),
        new Asset('buildingF', AssetType.MESH, 'assets/model/city_commercial/', 'building-f.glb'),
        new Asset('buildingG', AssetType.MESH, 'assets/model/city_commercial/', 'building-g.glb'),
        new Asset('buildingH', AssetType.MESH, 'assets/model/city_commercial/', 'building-h.glb'),
        new Asset('buildingI', AssetType.MESH, 'assets/model/city_commercial/', 'building-i.glb'),
        new Asset('buildingJ', AssetType.MESH, 'assets/model/city_commercial/', 'building-j.glb'),
        new Asset('buildingK', AssetType.MESH, 'assets/model/city_commercial/', 'building-k.glb'),
        new Asset('buildingL', AssetType.MESH, 'assets/model/city_commercial/', 'building-l.glb'),
        new Asset('buildingM', AssetType.MESH, 'assets/model/city_commercial/', 'building-m.glb'),
        new Asset('buildingN', AssetType.MESH, 'assets/model/city_commercial/', 'building-n.glb'),
        new Asset('cloud', AssetType.MESH, 'assets/model/platformer_cute/', 'cloud.glb'),
      ],
      assetObjects: [
        new AssetObject('treeA').withType('trees'),
        new AssetObject('treeB').withType('trees'),
        new AssetObject('treeC').withType('trees'),
        new AssetObject('treeD').withType('trees'),
        new AssetObject('treeE').withType('trees'),
        new AssetObject('treeF').withType('trees'),
        new AssetObject('treeG').withType('trees'),
        new AssetObject('treeH').withType('trees'),
        new AssetObject('rail').withType('rail'),
        new AssetObject('billboard').withType('billboard'),
        new AssetObject('flag').withType('flag'),
        new AssetObject('stand').withType('stand'),
        new AssetObject('rockA').withType('rockA'),
        new AssetObject('rockB').withType('rockB'),
        new AssetObject('dirt').withType('dirt'),
        new AssetObject('flowerYellow').withType('nature'),
        new AssetObject('flowerRed').withType('nature'),
        new AssetObject('flowerPurple').withType('nature'),
        new AssetObject('grass').withType('nature'),
        new AssetObject('log').withType('nature'),
        new AssetObject('stones').withType('stones'),
        new AssetObject('buildingA').withType('building'),
        new AssetObject('buildingB').withType('building'),
        new AssetObject('buildingC').withType('building'),
        new AssetObject('buildingD').withType('building'),
        new AssetObject('buildingE').withType('building'),
        new AssetObject('buildingF').withType('building'),
        new AssetObject('buildingG').withType('building'),
        new AssetObject('buildingH').withType('building'),
        new AssetObject('buildingI').withType('building'),
        new AssetObject('buildingJ').withType('building'),
        new AssetObject('buildingK').withType('building'),
        new AssetObject('buildingL').withType('building'),
        new AssetObject('buildingM').withType('building'),
        new AssetObject('buildingN').withType('building'),
        new AssetObject('cloud').withType('cloud'),
      ],
      distributions: [
        {
          types: ['nature'],
          mode: 'zone',
          zone: { min: wayConfig.width / 2, max: wayConfig.width * 2 },
          density: 1,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0,
          skipOccupied: true,
          offset: new Vector3(0, -1, 0),
          rolling: true,
        },

        {
          types: ['trees'],
          mode: 'zone',
          zone: { min: wayConfig.width / 2, max: wayConfig.width * 2 },
          density: 1,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0,
          skipOccupied: true,
          offset: new Vector3(0, -1, 0),
          rolling: true,
        },

        {
          types: ['nature'],
          mode: 'zone',
          zone: { min: -wayConfig.width / 2, max: -wayConfig.width * 1.05 },
          density: 2,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipProbability: 0,
          skipOccupied: true,
          offset: new Vector3(0, -1, 0),
          rolling: true,
        },

        {
          types: ['trees'],
          mode: 'zone',
          zone: { min: 40, max: 60 },
          density: 5,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 3.6, max: 7.2 },
          skipProbability: 0,
          skipOccupied: true,
          offset: new Vector3(0, -1, 0),
          rolling: true,
        },

        // {
        //   types: ['cloud'],
        //   mode: 'zone',
        //   zone: { min: -500, max: 500 },
        //   density: 2,
        //   interval: 2,
        //   alpha: 0.5,
        //   offset: new Vector3(0, 40, 0),
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 4, max: 12 },
        //   skipProbability: 0,
        //   skipOccupied: false,
        // },

        {
          types: ['stones'],
          mode: 'zone',
          alignToPath: true,
          offset: new Vector3(0, 0.1, 0),
          zone: { min: -wayConfig.width / 2.5, max: wayConfig.width / 2.5 },
          density: 1,
          interval: 4,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1.8, max: 3.6 },
          skipOccupied: true,
          skipProbability: 0.2,
        },

        // {
        //   types: ['rockA', 'rockB'],
        //   mode: 'zone',
        //   zone: { min: -50, max: 50 },
        //   deadZone: { min: -6, max: 6 },
        //   density: 5,
        //   interval: 1,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 1.8, max: 3.6 },
        //   skipProbability: 0.2,
        //   useLevelYPosition: true,
        // },

        // {
        //   types: ['rail'],
        //   mode: 'fixed',
        //   offset: new Vector3(wayConfig.width / 2, 0, 0),
        //   //rotationOffset: Math.PI,
        //   interval: 1,
        //   minPoint: 5,
        //   alignToPath: true,
        //   skipOccupied: true,
        //   scale: { min: 2, max: 2 },
        // },

        // {
        //   types: ['rail'],
        //   mode: 'fixed',
        //   offset: new Vector3(-wayConfig.width / 2, 0, 0),
        //   rotationOffset: Math.PI,
        //   interval: 1,
        //   minPoint: 5,
        //   skipOccupied: true,
        //   alignToPath: true,
        //   scale: { min: 2, max: 2 },
        // },

        // {
        //   types: ['billboard'],
        //   mode: 'fixed',
        //   offset: new Vector3(-8, 0, 0),
        //   rotationOffset: Math.PI,
        //   interval: 22,
        //   minPoint: 10,
        //   alignToPath: true,
        //   useLevelYPosition: true,
        //   scale: { min: 6, max: 6 },
        // },
        {
          types: ['building'],
          mode: 'fixed',
          offset: new Vector3(30, -1, 0),
          rotationY: { min: -Math.PI / 2, max: -Math.PI / 2 },
          interval: 10,
          minPoint: 10,
          alignToPath: false,
          scale: { min: 10, max: 10 },
          rolling: true,
          physics: false,
        },
        {
          types: ['building'],
          mode: 'fixed',
          offset: new Vector3(15, -1, 0),
          rotationY: { min: -Math.PI / 2, max: -Math.PI / 2 },
          interval: 8,
          minPoint: 10,
          alignToPath: false,
          scale: { min: 10, max: 10 },
          rolling: true,
          physics: false,
        },
        // {
        //   types: ['stand'],
        //   mode: 'fixed',
        //   offset: new Vector3(-2, 0, 0),
        //   rotationOffset: -Math.PI,
        //   rotationY: { min: Math.PI / 2, max: Math.PI / 2 },
        //   interval: 18,
        //   minPoint: 10,
        //   alignToPath: false,
        //   useLevelYPosition: true,
        //   scale: { min: 16, max: 16 },
        // },

        // {
        //   types: ['flag'],
        //   mode: 'fixed',
        //   offset: new Vector3(8.5, 0, 0),
        //   rotationOffset: Math.PI,
        //   interval: 12,
        //   alignToPath: true,
        //   rolling: true,
        //   skipOccupied: true,
        //   scale: { min: 4, max: 4 },
        // },
      ],
    };

    const minPoint = 10;

    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: wayConfig.width,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'yellow',
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: wayConfig.width,
      spawnRange: 60,
      despawnRange: 90,
    });

    // grounds and ceilings matrices should match on their edges, ensure this
    const groundWidth = 200;
    const groundHeight = 1500;
    const groundSubdivisions = 100;

    const heightMapConfig = HeightMapConfig.builder()
      .name('heightmap-ground')
      .heightMapImageUrl('assets/heightmap/snowdon.png')
      //.heightMapImageUrl('assets/heightmap/ambientocclusion.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .maxHeight(50)
      .minHeight(0)
      .metadata({
        centerOnPath: false,
      })
      .pathWidth(20) // Width of the corridor
      .pathTransitionWidth(7) // Steepness of the walls (greater = gentler)
      .pathYOffset(39) // Optional: Y-offset
      .terrainTextureName1('Grass1_Light')
      .terrainTextureName2('Grass3_Light')
      .terrainTextureName3('Grass5_Light')
      .terrainTextureScale(new Vector3(50, 50, 50))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, -40, 0))
      .build();

    const playerConfig: PlayerConfig = {
      startPathIndex: 4,
      startOffset: new Vector3(0, 3, 0),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const atlasConfig = [
      new TextureAtlas('tiles2', 'assets/atlas/', 'tiles2.png', 'tiles2.json'),
      new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json'),
    ];

    const spriteConfig = [
      {
        asset: new Asset('palm_cartoon', AssetType.IMAGE, 'assets/sprites/', 'palm_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1088,
        cellHeight: 1200,
        name: 'palm_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
      {
        asset: new Asset('tree_cartoon', AssetType.IMAGE, 'assets/sprites/', 'tree_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1136,
        cellHeight: 1200,
        name: 'tree_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
    ];

    const config = new LevelConfig(
      'Test Level',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      -45, // killing height
      100, // height above ground
      40, // checkpoint interval
      4, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    config.fog = fogConfig;
    config.way = wayConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.ground = groundConfig;
    config.decoration = decorationConfig;
    config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    //config.water = waterConfig;
    config.atlasses = atlasConfig;
    //config.sprites = spriteConfig;

    return config;
  }
}
